import { FormControl, Select, MenuItem, InputLabel, ListSubheader, TextField, Box } from "@mui/material";
import { Controller, UseFormReturn } from "react-hook-form";
import { DYNAMIC_AND_STATIC_DATE_OPERATORS, isPickedDateValue, parsePickedDateValue, PICKED_DATE_PREFIX } from "./dynamicDateUtils";

type DynamicDateValueFieldsProps = {
	entityForm: UseFormReturn;
	index: number;
};

export const DynamicDateValueFields = ({ entityForm, index }: DynamicDateValueFieldsProps) => {
	const watchedFields = entityForm.watch();
	const operator = watchedFields?.filters?.[index]?.operator;
	const isOperatorRelational = DYNAMIC_AND_STATIC_DATE_OPERATORS.includes(operator);

	const daySelection = [
		{
			subheaderName: "Dia",
			choices: [
				{ label: "Hoje", value: "today" },
				{ label: "Ontem", value: "yesterday" },
				{ label: "Anteontem", value: "before_yesterday" },
				{ label: "Amanhã", value: "tomorrow" },
				{ label: "Dia depois de amanhã", value: "after_tomorrow" },
			],
		},
	];
	const periodSelection = [
		{
			subheaderName: "Semana",
			choices: [
				{ label: "Esta Semana", value: "this_week" },
				{ label: "A Última Semana", value: "last_week" },
				{ label: "A Penúltima Semana", value: "previous_previous_week" },
				{ label: "A Próxima Semana", value: "next_week" },
				{ label: "A Segunda Próxima Semana", value: "next_next_week" },
			],
		},
		{
			subheaderName: "Mês",
			choices: [
				{ label: "Este Mês", value: "this_month" },
				{ label: "O Último Mês", value: "last_month" },
				{ label: "O Penúltimo Mês", value: "previous_previous_month" },
				{ label: "O Próximo Mês", value: "next_month" },
				{ label: "O Segundo Próximo Mês", value: "next_next_month" },
				{ label: "Este Mês e o Anterior", value: "this_and_last_month" },
				{ label: "Este Mês e o Próximo", value: "this_and_next_month" },
			],
		},
	];

	const relationalPeriodSelection = [
		{
			subheaderName: "Semana",
			choices: [
				{ label: "Esta Semana", value: "this_week" },
				{ label: "A Próxima Semana", value: "next_week" },
				{ label: "A Semana Anterior", value: "last_week" },
			],
		},
		{
			subheaderName: "Mês",
			choices: [
				{ label: "Este Mês", value: "this_month" },
				{ label: "O Próximo Mês", value: "next_month" },
				{ label: "O Mês Anterior", value: "last_month" },
			],
		},
	];

	const customDaysSelection = [
		{
			subheaderName: "Período Personalizado",
			choices: [
				{ label: "Últimos dias", value: `${PICKED_DATE_PREFIX}last` },
				{ label: "Próximos dias", value: `${PICKED_DATE_PREFIX}next` },
			],
		},
	];

	if (operator === "equal") {
		return (
			<DynamicDateDropdownWithDaysInput
				entityForm={entityForm}
				index={index}
				sections={[...daySelection, ...periodSelection, ...customDaysSelection]}
				inputLabel="Escolher o período"
			/>
		);
	}
	if (isOperatorRelational) {
		return (
			<DynamicDateDropdown
				entityForm={entityForm}
				index={index}
				sections={[...daySelection, ...relationalPeriodSelection]}
				inputLabel="Escolher o período"
			/>
		);
	}

	return null;
};

type SelectSection = {
	choices: Array<{ label: string; value: string }>;
	subheaderName: string;
};

type DynamicDateDropdownProps = {
	entityForm: UseFormReturn;
	index: number;
	sections: Array<SelectSection>;
	inputLabel: string;
};

export const DynamicDateDropdown = ({ entityForm, index, sections, inputLabel }: DynamicDateDropdownProps) => {
	const hasHeaders = sections.length > 1;

	const renderSection = (section: SelectSection, sectionIndex: number) => [
		...(hasHeaders ? [<ListSubheader key={`h-${sectionIndex}`}>{section.subheaderName}</ListSubheader>] : []),
		...section.choices.map((choice) => (
			<MenuItem key={choice.value} value={choice.value}>
				{choice.label}
			</MenuItem>
		)),
	];

	return (
		<Controller
			name={`filters.${index}.value`}
			control={entityForm.control}
			render={({ field }) => (
				<FormControl fullWidth>
					<InputLabel>{inputLabel}</InputLabel>
					<Select
						{...field}
						value={field.value ?? ""}
						label={inputLabel}
						onChange={(event) => field.onChange(event.target.value)}
						sx={{ minHeight: "57px" }}
					>
						{sections.map(renderSection)}
					</Select>
				</FormControl>
			)}
		/>
	);
};

const getPickedSelectionType = (value: string) => {
	if (!value || typeof value !== "string" || !value.startsWith(PICKED_DATE_PREFIX)) return null;
	
	if (isPickedDateValue(value)) {
		const { direction } = parsePickedDateValue(value);
		return direction;
	}
	
	const partialType = value.replace(PICKED_DATE_PREFIX, "");
	if (partialType === "last" || partialType === "next") {
		return partialType;
	}
	
	return null;
};

const getDaysFromPickedValue = (value: string) => {
	if (typeof value !== "string" || !isPickedDateValue(value)) return "";
	const { days } = parsePickedDateValue(value);
	return isNaN(days) ? "" : days.toString();
};

const getSelectValueFromFullValue = (value: string) => {
	if (!value || typeof value !== "string") return "";
	
	if (isPickedDateValue(value)) {
		const { direction } = parsePickedDateValue(value);
		const selectValue = `${PICKED_DATE_PREFIX}${direction}`;
		return selectValue;
	}
	
	return value;
};

export const DynamicDateDropdownWithDaysInput = ({ entityForm, index, sections, inputLabel }: DynamicDateDropdownProps) => {
	const hasHeaders = sections.length > 1;
	const currentValue = entityForm.watch(`filters.${index}.value`) ?? "";
	const pickedSelectionType = getPickedSelectionType(currentValue);
	const showDaysInput = pickedSelectionType !== null;

	const renderSection = (section: SelectSection, sectionIndex: number) => [
		...(hasHeaders ? [<ListSubheader key={`h-${sectionIndex}`}>{section.subheaderName}</ListSubheader>] : []),
		...section.choices.map((choice) => (
			<MenuItem key={choice.value} value={choice.value}>
				{choice.label}
			</MenuItem>
		)),
	];

	const handleSelectChange = (newSelectValue: string, fieldOnChange: (value: string) => void) => {
		fieldOnChange(newSelectValue);
	};

	const handleDaysChange = (daysValue: string, fieldOnChange: (value: string) => void) => {
		if (!pickedSelectionType) return;
		
		const numericDays = daysValue.replace(/\D/g, "");
		if (numericDays === "") {
			const partialValue = `${PICKED_DATE_PREFIX}${pickedSelectionType}`;
			fieldOnChange(partialValue);
		} else {
			const fullValue = `${PICKED_DATE_PREFIX}${numericDays}_${pickedSelectionType}`;
			fieldOnChange(fullValue);
		}
	};

	return (
		<Controller
			name={`filters.${index}.value`}
			control={entityForm.control}
			render={({ field }) => (
				<Box sx={{ display: "flex", gap: 1 }}>
					<FormControl fullWidth>
						<InputLabel>{inputLabel}</InputLabel>
						<Select
							value={getSelectValueFromFullValue(field.value ?? "")}
							label={inputLabel}
							onChange={(event) => handleSelectChange(event.target.value, field.onChange)}
							sx={{ minHeight: "57px" }}
						>
							{sections.map(renderSection)}
						</Select>
					</FormControl>
					{showDaysInput && (
						<TextField
							label="Dias"
							type="number"
							value={getDaysFromPickedValue(field.value ?? "")}
							onChange={(event) => handleDaysChange(event.target.value, field.onChange)}
							sx={{ minWidth: 80, maxWidth: 100 }}
							inputProps={{ min: 1 }}
						/>
					)}
				</Box>
			)}
		/>
	);
};
